import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { w as waitForAnimation } from './index3.js';
import { d as defineCustomElement$5 } from './close-button.js';
import { d as defineCustomElement$4 } from './icon.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const slideOutCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;bottom:0;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;left:0;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:fixed;right:0;top:0;z-index:var(--jw-z-index-overlays)}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host .container{align-items:center;bottom:0;display:flex;justify-content:center;left:0;min-height:100%;pointer-events:none;position:relative;right:0;top:0;z-index:var(--jw-z-index-overlays)}:host .backdrop{background-color:#000000b3;bottom:0;left:0;opacity:0;pointer-events:none;position:fixed;right:0;top:0;transition:opacity .25s ease;z-index:calc(var(--jw-z-index-overlays) - 1)}:host .backdrop.-anim-fade-in{animation:fade-in .25s linear forwards;pointer-events:all}@keyframes fade-in{0%{opacity:0}to{opacity:1}}:host .backdrop.-anim-fade-out{animation:fade-out .25s linear forwards}@keyframes fade-out{0%{opacity:1}to{opacity:0}}:host .content{pointer-events:all;z-index:calc(var(--jw-z-index-overlays) + 1)}.content{background-color:var(--jw-theme-surface-tile-bg-color);bottom:0;color:var(--jw-theme-surface-text-default-color);display:flex;flex-direction:column;height:100%;position:absolute;right:0;top:0;transform:translateX(100%);transition:box-shadow .2s linear .01s;width:var(--jw-container-lg)}@keyframes wui-slide-out-open{0%{opacity:0;transform:translateX(100%)}90%{opacity:1}to{opacity:1;transform:translateX(0)}}@keyframes wui-slide-out-close{0%{opacity:1;transform:translateX(0)}90%{opacity:0}to{opacity:0;transform:translateX(100%)}}.content.-anim-open{animation:wui-slide-out-open .5s cubic-bezier(.25,.46,.45,.94) forwards}.content.-anim-close{animation:wui-slide-out-close .5s cubic-bezier(.25,.46,.45,.94) forwards}.header{align-items:center;border-bottom:1px solid var(--jw-theme-surface-border-border-color);color:var(--jw-theme-surface-text-default-color);display:flex;flex:none;justify-content:space-between;padding:1.5rem}.header h2{font-family:Open Sans;font-size:1.25rem;font-weight:400;letter-spacing:-.0008em;line-height:1.75rem;margin:0;padding:0}.body{display:flex;flex:1;overflow-y:auto;padding:1.5rem}.body p{margin-top:0!important}.footer{align-items:center;border-top:1px solid var(--jw-theme-surface-border-border-color);display:flex;flex:none;gap:1rem;justify-content:flex-end;padding:1.5rem}:host([data-display=open]) .content{box-shadow:var(--jw-theme-effects-shadow-shadow-slideout-box-shadow)}::slotted([slot]){display:flex;flex:1;flex-direction:column}::slotted([slot=footer]){display:flex;flex:none;flex-direction:row;gap:1rem}";
const WuiSlideOutStyle0 = slideOutCss;

const WuiSlideOut$1 = /*@__PURE__*/ proxyCustomElement(class WuiSlideOut extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.displayChanged = createEvent(this, "displayChanged", 3);
        this.animate = async ({ backdrop, content, }) => {
            const shadowRoot = this.el.shadowRoot;
            if (!shadowRoot) {
                return;
            }
            const promise = Promise.all([
                backdrop ? waitForAnimation(shadowRoot.querySelector('.content')) : Promise.resolve(),
                content ? waitForAnimation(shadowRoot.querySelector('.backdrop')) : Promise.resolve(),
            ]);
            if (backdrop) {
                this.backdropAnimClass = backdrop;
            }
            if (content) {
                this.contentAnimClass = content;
            }
            await promise;
        };
        this.handleDismiss = () => {
            // Can't dismiss blocking slide-outs
            if (this.blocking) {
                return;
            }
            this._setDisplay('closed');
        };
        this.uid = undefined;
        this.api = undefined;
        this.headerTitle = '';
        this.backdrop = true;
        this.theme = 'light';
        this.sections = ['header', 'body', 'footer'];
        this.blocking = false;
        this.contentAnimClass = '';
        this.backdropAnimClass = '';
        this.display = 'closed';
    }
    /**
     * Set display state.
     *
     * Possible values: `'open'`, `'closed'`.
     */
    async setDisplay(value) {
        if (value === this.display) {
            return;
        }
        return this._setDisplay(value);
    }
    _setDisplay(value) {
        return new Promise((resolve) => {
            window.requestAnimationFrame(() => {
                switch (value) {
                    case 'open':
                        this.animate({
                            backdrop: '-anim-fade-in',
                            content: '-anim-open',
                        }).then(() => {
                            this.displayChanged.emit(this.display);
                            resolve();
                        });
                        this.display = 'open';
                        break;
                    case 'closed':
                        this.animate({
                            backdrop: '-anim-fade-out',
                            content: '-anim-close',
                        }).then(() => {
                            // Wait for animations to finish before resetting state
                            window.requestAnimationFrame(() => {
                                this.display = 'closed';
                                this.displayChanged.emit(this.display);
                                resolve();
                            });
                        });
                        break;
                    default:
                        resolve();
                        break;
                }
            });
        });
    }
    render() {
        const { headerTitle, backdrop, blocking, uid, display, contentAnimClass, backdropAnimClass } = this;
        return (h(Host, { key: 'a67c6fa89df7faaec2d0e73fdc7dc23c584b4811', id: uid ? `wui_slide_out__${uid}` : '', "data-display": display, theme: this.theme }, backdrop && h("div", { key: 'dcaec26a6dd85171c26fae4af9e2cbdbbb0a2155', class: `backdrop ${backdropAnimClass}`, role: "button", onClick: this.handleDismiss }), h("div", { key: '276a288c0b02ad63c8112dac2b4adbd234775b33', class: "container" }, h("div", { key: '5d2009ef2ba0528f0ebf93d02ec4646e311a6b8e', class: `content ${contentAnimClass}` }, (!this.sections || this.sections.includes('header')) && (h("div", { key: '242b16f1b212ffd42767f50803ceff84e47070fd', class: "header" }, h("h2", { key: 'bd981ff24ef22100e659b3ac9b630c95a24b71e0' }, headerTitle), !blocking && h("wui-close-button", { key: 'ebe06e9572816e40cc6883776c5eb418d36be502', onClick: this.handleDismiss }))), (!this.sections || this.sections.includes('body')) && (h("div", { key: '82bd16b1638f3ca720072274fd00c77028e2ea06', class: "body" }, h("slot", { key: 'd340dea2257eebcadb83dc83f02aafc6b8e87890', name: "body" }))), (!this.sections || this.sections.includes('footer')) && (h("div", { key: '1070b6561af584da7c67eeac5d3796646eaf6fc3', class: "footer" }, h("slot", { key: '48120a2ff01dc00fcf49a154a92b5f6b19a43722', name: "alert" }), h("slot", { key: '777782679816c71e0cdd682f8a6425cbb5b69cc8', name: "footer" })))))));
    }
    get el() { return this; }
    static get style() { return WuiSlideOutStyle0; }
}, [1, "wui-slide-out", {
        "uid": [1],
        "api": [16],
        "headerTitle": [1, "header-title"],
        "backdrop": [4],
        "theme": [1],
        "sections": [16],
        "blocking": [4],
        "contentAnimClass": [32],
        "backdropAnimClass": [32],
        "display": [32],
        "setDisplay": [64]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-slide-out", "wui-close-button", "wui-icon", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-slide-out":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiSlideOut$1);
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiSlideOut = WuiSlideOut$1;
const defineCustomElement = defineCustomElement$1;

export { WuiSlideOut, defineCustomElement };

//# sourceMappingURL=wui-slide-out.js.map